import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Logger } from '../../../infrastructure/logging/logger';
import { Organization } from '../models/Organization';
import { OrganizationService } from '../services/organizations.service';
import { OrgBasicInfoComponent } from './tabs/org-basic-info/org-basic-info.component';
import { OrgAddressComponent } from './tabs/org-address/org-address.component';
import { OrgMembersComponent } from './tabs/org-members/org-members.component';
import { OrgAssignedPoliciesComponent } from './tabs/org-assigned-policies/org-assigned-policies.component';
import { OrgVerificationPackagesComponent } from './tabs/org-verification-packages/org-verification-packages.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { DeactivatePackageDialogComponent } from '../../verification-packages/verification-package-detail/components/deactivate-package-dialog/deactivate-package-dialog.component';
import { VerificationPackagesService } from '../../verification-packages/services/verification-packages.service';
import { ErrorActivatingOrgDialogComponent } from './components/error-activating-org-dialog/error-activating-org-dialog.component';
import { Policy } from '../../policies/models/Policy';
import { VerificationPackage } from '../../verification-packages/models/VerificationPackage';
import { AccountService } from '../../../shared/account/account.service';
import { ReferenceService } from '../../../shared/reference/reference.service';
import { State } from '../../../shared/address/State';

@Component({
  selector: 'app-organization-detail',
  templateUrl: './organization-detail.component.html',
  styleUrls: ['./organization-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatCard,
    MatTabsModule,
    MatProgressSpinner,
    MatDialogModule,
    MatButtonModule,
    OrgBasicInfoComponent,
    OrgAddressComponent,
    OrgMembersComponent,
    OrgAssignedPoliciesComponent,
    OrgVerificationPackagesComponent,
    BackButtonComponent
  ]
})
export class OrganizationDetailComponent implements OnInit {

  @ViewChild(OrgMembersComponent) orgMembersComponent!: OrgMembersComponent;
  @ViewChild(OrgAssignedPoliciesComponent) assignedPoliciesComponent!: OrgAssignedPoliciesComponent;
  @ViewChild(OrgVerificationPackagesComponent) verificationPackagesComponent!: OrgVerificationPackagesComponent;

  private routeSub?: Subscription;

  loading = new BehaviorSubject<boolean>(false);
  states: State[] = [];
  organization!: Organization;
  organizationId!: string;
  tabIndex: number = 0;
  deactivateOrganization: boolean = false;
  organizationIsActiveOrInactive: boolean = true;
  selectedIndex = 0;
  embeddedOrganizationBoolean: boolean = false;

  organizationPolicies: Policy[] = [];
  organizationVerificationPackages: VerificationPackage[] = [];

  constructor(
    private organizationService: OrganizationService,
    private verificationPackagesService: VerificationPackagesService,
    private accountService: AccountService,
    private referenceService: ReferenceService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private logger: Logger
  ) { }

  ngOnInit() {
    if (history.state.row) {
      this.embeddedOrganizationBoolean = true;
      this.organizationId = history.state.row['organizationId'];
      this.loadEmbeddedOrganization(history.state.row);
    } else {
      this.routeSub = this.route.params.subscribe(params => {
        this.embeddedOrganizationBoolean = false;
        this.organizationId = params['id'];
        this.loadPage(params['id']);
      });
    }
  }

  async loadEmbeddedOrganization(row: any) {
    try {
      this.loading.next(true);
      const partnerOrganization = await this.organizationService.getPartnerOrganizationById(row.organizationId);
      this.organization = {
        ...row,
        pillStatus: partnerOrganization.status,
        statusReason: partnerOrganization.statusReason,
        coverageStartDate: partnerOrganization.coverageStartDate,
        coverageEndDate: partnerOrganization.coverageEndDate
      };
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while retrieving the latest Partner Organization status', 'Error while retrieving the latest Partner Organization status');
      this.organization = row;
    } finally {
      this.loading.next(false);
    }
  }

  async loadPage(id: string) {
    try {
      this.loading.next(true);

      this.states = this.referenceService.getAllOrganizationAddressStates();
      this.organization = await this.organizationService.getOrganizationDetail(id);
      this.findPoliciesByOrganizationId(Number(this.organization.id));
      this.findVerificationPackagesByOrgId(Number(this.organization.id));

      if (this.organization.status_id === 1) {
        this.deactivateOrganization = true;
        this.organizationIsActiveOrInactive = true;
      }
      if (this.organization.status_id === 2) {
        this.deactivateOrganization = false;
        this.organizationIsActiveOrInactive = true;
      }
      if (this.organization.status_id === 3 || this.organization.status_id === 4) {
        this.deactivateOrganization = false;
        this.organizationIsActiveOrInactive = false;
      }
    } catch(e) {
      this.logger.handleError(e, 'An error occurred while retrieving Organization details', 'Error while retrieving Organization details');
    } finally {
      this.loading.next(false);
    }
  }

  onOrganizationUpdated() {
    this.loadPage(this.organizationId);
  }

  onOrganizationMemberDetailUpdate() {
    this.loadPage(this.organizationId);
    this.selectedIndex = 2;
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  async findPoliciesByOrganizationId(id: number) {
    const policyResponse = await this.organizationService.getPoliciesByOrganizationId(id);
    this.organizationPolicies = policyResponse.filter(policy => policy.status_id === 1);
  }

  async findVerificationPackagesByOrgId(organizationId: number) {
    this.organizationVerificationPackages = await this.verificationPackagesService.getVerificationPackagesByOrgId(organizationId);
  }

  onUpdateOrganizationStatusClick() {
    const dialogRef = this.dialog.open(DeactivatePackageDialogComponent, {
      width: '55%',
      height: '210px',
      disableClose: false,
      data: {
        component: 'Organization',
        organization: this.organization
      }
    });
    dialogRef.afterClosed().subscribe(async statusId => {
      if (statusId > 0) {
        try {
          let payload = {
            status_id: statusId
          }
          this.organizationService.updateOrganizationStatus(this.organization, payload).then(_ => {
            if (this.organization.members.length > 0 && statusId === 2) {
              let ctr = 0;
              this.organization.members.forEach(member => {
                ctr++;
                this.accountService.updateMemberAccountStatus(member.account_id, statusId);
                if (ctr === this.organization.members.length) {
                  this.orgMembersComponent.loadMembers();
                }
              });
            } 
            this.loadPage(this.organization.id);
          });
        } catch(e) {
          this.logger.handleError(e, 'An error occurred while updating Organization status', 'Error while updating Organization status');
        }
      }
    });
  }

  onStartActivationProcessClick() {
    if (this.organization.for_reservation && this.organization.for_guest_verification) {
      if (this.organizationPolicies.length > 0 && this.organizationVerificationPackages.length > 0) {
        this.onUpdateOrganizationStatusClick();
      } else {
        this.openErrorActivatingOrganizationDialog('Insurance Policy and Verification Package');
      }
    }

    if (!this.organization.for_reservation && this.organization.for_guest_verification) {
      if (this.organizationVerificationPackages.length > 0) {
        this.onUpdateOrganizationStatusClick();
      } else {
        this.openErrorActivatingOrganizationDialog('Verification Package');
      }
    }

    if (this.organization.for_reservation && !this.organization.for_guest_verification) {
      if (this.organizationPolicies.length > 0) {
        this.onUpdateOrganizationStatusClick();
      } else {
        this.openErrorActivatingOrganizationDialog('Insurance Policy');
      }
    }
  }

  openErrorActivatingOrganizationDialog(missingComponents: string) {
    this.dialog.open(ErrorActivatingOrgDialogComponent, {
      width: '750px',
      maxHeight: '275px',
      disableClose: true,
      data: {
        organizationId: this.organization.id,
        orgMissingComponents: missingComponents
      }
    });
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.tabIndex = tabChangeEvent.index;
  }

  onAssignPolicyClick() {
    this.assignedPoliciesComponent.onAssignPolicyClick();
  }

  onAssignVerificationPackageClick() {
    this.verificationPackagesComponent.onAssignVerificationPackageClick();
  }
}